/**
 * 💰 AccessControlService
 * Centrally manages permissions and feature gating across the Wingman platform.
 */

export type UserTier = 'free' | 'pro' | 'elite';

export type Feature = 
    | 'VIBE_MODE_SCROLL' 
    | 'LIVE_VIBE' 
    | 'PREMIUM_MEDIA' 
    | 'SAVED_VAULT' 
    | 'SPATIAL_RESEARCH';

interface TierLimits {
    vibeScrollLimit: number;
    vaultLimit: number;
    hasLiveAccess: boolean;
    hasPremiumMedia: boolean;
}

const TIER_CONFIG: Record<UserTier, TierLimits> = {
    'free': {
        vibeScrollLimit: 3,
        vaultLimit: 5,
        hasLiveAccess: false,
        hasPremiumMedia: false
    },
    'pro': {
        vibeScrollLimit: 10,
        vaultLimit: 50,
        hasLiveAccess: true,
        hasPremiumMedia: true
    },
    'elite': {
        vibeScrollLimit: Infinity,
        vaultLimit: Infinity,
        hasLiveAccess: true,
        hasPremiumMedia: true
    }
};

class AccessControlService {
    private static instance: AccessControlService;

    private constructor() {}

    public static getInstance(): AccessControlService {
        if (!AccessControlService.instance) {
            AccessControlService.instance = new AccessControlService();
        }
        return AccessControlService.instance;
    }

    public canAccess(feature: Feature, tier: UserTier, context?: any): boolean {
        const config = TIER_CONFIG[tier];

        switch (feature) {
            case 'VIBE_MODE_SCROLL':
                const scrollIndex = context?.index || 0;
                return scrollIndex < config.vibeScrollLimit;
            
            case 'SAVED_VAULT':
                const currentSavedCount = context?.count || 0;
                return currentSavedCount < config.vaultLimit;

            case 'LIVE_VIBE':
                return config.hasLiveAccess;

            case 'PREMIUM_MEDIA':
                return config.hasPremiumMedia;

            case 'SPATIAL_RESEARCH':
                // 🛡️ SSOT DELEGATION: Backend enforces quota limits.
                // The frontend no longer gates SPATIAL_RESEARCH locally.
                // Instead, the backend returns QUOTA_EXCEEDED when the user
                // has exhausted their tier's daily query limit.
                // The frontend listens for WINGMAN_QUOTA_EXCEEDED events
                // (dispatched by contextEngine.ts) to trigger the upgrade prompt.
                // This prevents frontend/backend drift on entitlement logic.
                return true; // Always allow — backend enforces actual limits

            default:
                return false;
        }
    }

    public getUpgradeMessage(feature: Feature): string {
        switch (feature) {
            case 'VIBE_MODE_SCROLL':
                return "You've reached the free scroll limit. Upgrade to unlock the infinite cinematic loop.";
            case 'LIVE_VIBE':
                return "Trending Live Intelligence is an Elite feature. Upgrade to see what's happening now.";
            case 'SAVED_VAULT':
                return "Your Spatial Vault is full. Upgrade to save more locations.";
            case 'PREMIUM_MEDIA':
                return "4K Cinematic Intelligence requires an active subscription.";
            default:
                return "This feature requires a premium tier.";
        }
    }
}

export const accessControl = AccessControlService.getInstance();
